import React from "react";
import { OrderLineTypes, TicketTypes } from "@repo/database";
import { formatIntToCurrency, formatNewLineText, groupBy } from "@repo/utils";
import { format, isAfter, isBefore } from "date-fns";
import { useTranslation } from "react-i18next";

import type { ReceiptReport } from "~/managers/use-register-manager";
import { useConfiguration } from "~/hooks/use-configuration";
import { useUser } from "~/hooks/use-user";

export default function ReceiptComponent({
  ref,
  receipt,
}: {
  ref: React.RefObject<HTMLDivElement>;
  receipt: ReceiptReport["data"][number];
}) {
  const { receiptSettings } = useConfiguration();

  const { user } = useUser();

  const { t } = useTranslation("common");

  const { t: userT } = useTranslation("common", { lng: user.language });

  const ticketSettings = receiptSettings.find(
    (obj) => obj.type === TicketTypes.receipt,
  );

  const totalDiscount = receipt.orderLines
    .filter((item) => item.type === OrderLineTypes.discount)
    .reduce((acc, item) => acc + item.value * item.quantity, 0);

  const totalQuantity = receipt.orderLines
    .filter((orderLine) => orderLine.type === OrderLineTypes.product)
    .reduce((acc, item) => acc + item.quantity, 0);

  const groupedByReference = groupBy(receipt.orderLines, "reference");

  const filteredItems = Object.values(groupedByReference).flatMap((items) => {
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    return totalQuantity > 0 ? items : [];
  });

  const cardholderTickets = receipt.payments
    .map((payment) => payment.cardholderTicket)
    .filter((obj) => obj !== undefined);

  const paymentDetails = receipt.payments
    .filter((payment) => payment.cardNumber)
    .map((payment) => ({
      cardLabel: payment.cardLabel,
      cardNumber: payment.cardNumber,
      paidAt: payment.paidAt,
      amount: payment.amount,
    }));

  const updatedProductsV2 = filteredItems
    .filter(
      (item) =>
        item.type === OrderLineTypes.product ||
        item.type === OrderLineTypes.extra,
    )
    .map((item) => {
      const itemValue = item.value * item.quantity;

      // Calculate discount already assigned to a specific item (if any)
      const itemDiscount = filteredItems
        .filter(
          (i) =>
            i.type === OrderLineTypes.discount &&
            i.parentReference === item.reference,
        )
        .reduce(
          (acc, discountItem) =>
            acc + discountItem.value * discountItem.quantity,
          0,
        );

      // Recalculate global discounts proportionally based on net value
      const addedDiscount = filteredItems
        .filter(
          (i) =>
            i.type === OrderLineTypes.discount &&
            i.parentReference === undefined,
        )
        .reduce((acc, discount) => {
          // Only apply the discount if it was created after the item was created
          if (isAfter(discount.createdAt, item.createdAt)) {
            // Compute the total net value of all eligible items
            const eligibleTotal = filteredItems
              .filter(
                (product) =>
                  product.type === OrderLineTypes.product &&
                  isBefore(product.createdAt, discount.createdAt),
              )
              .reduce(
                (total, product) => total + product.value * product.quantity,
                0,
              );

            // Allocate to the item a proportional share based on its net value
            if (eligibleTotal > 0) {
              return acc + discount.value * (itemValue / eligibleTotal);
            }
            return acc;
          }
          return acc;
        }, 0);

      return {
        ...item,
        totalValue: itemValue,
        discount: itemDiscount + addedDiscount,
      };
    });

  // Update products with proportional discounts
  const updatedProductsV1 = filteredItems
    .filter(
      (item) =>
        item.type === OrderLineTypes.product ||
        item.type === OrderLineTypes.extra,
    )
    .map((item) => {
      const itemValue = item.value * item.quantity;

      const itemDiscount = filteredItems
        .filter(
          (i) =>
            i.type === OrderLineTypes.discount &&
            i.parentReference === item.reference,
        )
        .reduce((acc, item) => acc + item.value * item.quantity, 0);
      // Calculate added discounts that apply to this item
      const addedDiscount = filteredItems
        .filter(
          (i) =>
            i.type === OrderLineTypes.discount &&
            i.parentReference === undefined,
        )
        .reduce((acc, discount) => {
          // Check if this discount applies to the item based on creation time
          if (isAfter(discount.createdAt, item.createdAt)) {
            // Find the total quantity of items eligible for this discount
            const eligibleQuantity = filteredItems
              .filter(
                (product) =>
                  product.type === OrderLineTypes.product &&
                  isBefore(product.createdAt, discount.createdAt),
              )
              .reduce((total, product) => total + product.quantity, 0);

            // Distribute the discount proportionally to this item
            if (eligibleQuantity > 0) {
              return acc + (discount.value * item.quantity) / eligibleQuantity;
            }
          }
          return acc;
        }, 0);

      return {
        ...item,
        totalValue: itemValue,
        discount: itemDiscount + addedDiscount,
      };
    });

  let updatedProducts = updatedProductsV2;

  if (receipt.createdAt < new Date(2025, 1, 23)) {
    updatedProducts = updatedProductsV1;
  }

  const groupedItems = groupBy(updatedProducts, "name");

  const groupedVat = groupBy(
    updatedProducts.filter((item) => item.vatRate !== 0),
    "vatRate",
  );

  // Prepare items for display
  const groupedItemsArray = Object.entries(groupedItems).map(
    ([name, items]) => ({
      name: name.split("-")[0],
      price: items.reduce((acc, item) => acc + item.value, 0),
      quantity: items.reduce((acc, item) => acc + item.quantity, 0),
      totalValue: items.reduce((acc, item) => acc + item.totalValue, 0),
      totalDiscount: items.reduce((acc, item) => acc + item.discount, 0),
      netAmount: items.reduce(
        (acc, item) => acc + item.totalValue - item.discount,
        0,
      ),
    }),
  );

  const totalProducts = updatedProducts.reduce(
    (acc, item) => acc + item.totalValue,
    0,
  );

  const netTotal = updatedProducts.reduce(
    (acc, item) => acc + item.totalValue - item.discount,
    0,
  );

  // Functions to calculate net total and VAT total per item
  const calculateNetTotal = (item: (typeof updatedProducts)[number]) => {
    const netAmount = item.totalValue - item.discount;
    return (netAmount * 100) / (100 + item.vatRate);
  };

  const calculateVatTotal = (item: (typeof updatedProducts)[number]) => {
    const netAmount = item.totalValue - item.discount;
    return (netAmount * item.vatRate) / (100 + item.vatRate);
  };

  return (
    <div
      ref={ref}
      className="flex flex-col py-6 w-full border px-2 bg-white text-black"
    >
      <div className="flex flex-col items-center">
        {ticketSettings?.logo?.url && (
          <img src={ticketSettings.logo.url} width="100px" />
        )}
        {ticketSettings?.header && (
          <span
            className="text-center"
            dangerouslySetInnerHTML={{
              __html: formatNewLineText(ticketSettings.header),
            }}
          ></span>
        )}
        <span className="text-2xl font-bold">
          {ticketSettings?.title ?? userT("Ticket.receipt")}
        </span>
      </div>
      <div className="flex flex-col py-2">
        <span>
          {t("Ticket.time", { time: format(receipt.createdAt, "Pp") })}
        </span>
        {receipt.employee && (
          <span>
            {t("Ticket.employee", { employee_name: receipt.employee })}
          </span>
        )}

        {receipt.table && (
          <span>{t("Ticket.table", { table_reference: receipt.table })}</span>
        )}
        <span>
          {t("Ticket.receipt_number", { receipt_number: receipt.number })}
        </span>
      </div>
      <div className="border-t border-b py-2">
        {groupedItemsArray
          .filter((obj) => obj.quantity !== 0)
          .map((item) => (
            <div key={item.name} className="flex flex-col gap-1">
              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-row items-center gap-2">
                  <span>{item.quantity}</span>
                  <span>{item.name}</span>
                </div>
                <span className="text-right">
                  {formatIntToCurrency(item.totalValue)}
                </span>
              </div>
            </div>
          ))}
      </div>

      <div className="flex flex-row gap-2 justify-between py-2">
        <span>{t("Ticket.quantity")}</span>
        <span>{totalQuantity}</span>
      </div>
      {totalDiscount !== 0 && (
        <div className="flex flex-col border-t py-2">
          <div className="flex flex-row justify-between ">
            <span>{userT("Ticket.subtotal")}</span>
            <span>{formatIntToCurrency(totalProducts, true)}</span>
          </div>

          <div className="flex flex-row justify-between">
            <span>{userT("Ticket.discount")}</span>
            <span>{`- ${formatIntToCurrency(totalDiscount, true)}`}</span>
          </div>
        </div>
      )}

      <div className="flex flex-row gap-2 justify-between text-xl font-bold border-t py-2">
        <span>{t("Ticket.total")}</span>
        <span>{formatIntToCurrency(netTotal, true)}</span>
      </div>

      {receipt.payments.length > 0 && (
        <div className="border-t py-2">
          {receipt.payments.map((payment) => (
            <>
              <div className="flex flex-row justify-between" key={payment.name}>
                <span>{payment.name} </span>
                <span>{formatIntToCurrency(payment.amount)}</span>
              </div>
              {payment.change > 0 && (
                <div
                  className="flex flex-row justify-between"
                  key={payment.name}
                >
                  <span>{userT("Ticket.change")}</span>
                  <span>{formatIntToCurrency(-payment.change)}</span>
                </div>
              )}
            </>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2 border-t">
        <div className="grid grid-cols-4 gap-2">
          <span>{userT("Ticket.vat_rate").toUpperCase()}</span>
          <span>{userT("Ticket.net_total").toUpperCase()}</span>
          <span className="text-right">
            {userT("Ticket.vat_total").toUpperCase()}
          </span>
          <span className="text-right">
            {userT("Ticket.gross_total").toUpperCase()}
          </span>
        </div>
        {Object.entries(groupedVat).map(([vatRate, items]) => {
          const netTotalVat = items.reduce(
            (acc, item) => acc + calculateNetTotal(item),
            0,
          );
          const vatTotal = items.reduce(
            (acc, item) => acc + calculateVatTotal(item),
            0,
          );
          const grossTotal = items.reduce(
            (acc, item) => acc + item.totalValue - item.discount,
            0,
          );

          return (
            <div className="grid grid-cols-4 gap-2" key={vatRate}>
              <span>{`${vatRate}%`}</span>
              <span>{formatIntToCurrency(netTotalVat, false)}</span>
              <span className="text-right">
                {formatIntToCurrency(vatTotal, false)}
              </span>
              <span className="text-right">
                {formatIntToCurrency(grossTotal, false)}
              </span>
            </div>
          );
        })}
      </div>
      {paymentDetails.length > 0 && (
        <div className="flex flex-col text-xs items-center gap-4 font-mono mt-4">
          <span className="text-base font-medium text-center w-full">
            Kopie kaarthouder
          </span>
          {paymentDetails.map((p) => (
            <div className="flex flex-col gap-2 items-center">
              <div className="flex flex-col gap-1 items-center">
                <span>{user.companyName}</span>
                <span>{user.city}</span>
              </div>
              <span className="uppercase">{`${p.cardLabel} (***********${p.cardNumber})`}</span>
              <div className="flex flex-col gap-1 items-center">
                <span>BETALING</span>
                {p.paidAt && (
                  <span className="uppercase">
                    {format(p.paidAt, "dd-MM-yyyy HH:mm")}
                  </span>
                )}
                <span>Totaal: {formatIntToCurrency(p.amount, false)}</span>
              </div>
              <span>AKKOORD</span>
            </div>
          ))}
        </div>
      )}

      {cardholderTickets.length > 0 && (
        <div className="mt-4 flex flex-col items-center">
          <span className="text-lg font-medium text-center w-full">
            Kopie kaarthouder
          </span>
          {cardholderTickets.map((cardholderTicket, idx) => {
            const decoded = atob(cardholderTicket);

            return (
              <div
                key={idx}
                style={{
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",

                  width: "90%", // Ensures it doesn't exceed the container
                  wordBreak: "break-word", // Breaks long words to fit the width
                  fontSize: "0.8rem", // Adjust the font size to make it smaller
                  lineHeight: "1.2", // Adjust line spacing for better readability
                }}
              >
                {decoded}
              </div>
            );
          })}
        </div>
      )}

      {ticketSettings?.footer && (
        <div
          className="text-center mt-4"
          dangerouslySetInnerHTML={{
            __html: formatNewLineText(ticketSettings.footer),
          }}
        ></div>
      )}
    </div>
  );
}
